import type { Deck } from '~~/entities/deck'
import { useToast } from '~/components/ui/toast'
import type { Slide } from '~~/entities/slide'
import { SlideTypes } from '~~/entities/slide'

export const PASSWORD_REQUIRED_HEADER_MSG = 'SCRAM-SHA-512 realm="Access to private deck with password"'

export class LegacyDeckError extends Error {
  constructor(message: string = 'This 3D Presentation contains legacy 3D model slides that are no longer supported.') {
    super(message)
    this.name = 'LegacyDeckError'
  }
}

const INCLUDE = ['company', 'site_image', 'model_camera', 'assets', 'able_to_book', 'inactive_meeting_slides']

export function useEditDeck({ id, onSetActiveSlide }: { id: string; onSetActiveSlide?: (slide: Slide) => void }) {
  const { getDeckById } = useDecks()
  const { currentMeeting } = useMeetings()
  const { bulkUpdateSlides } = useDeckSlides(id)

  const { toast } = useToast()

  const state = reactive<{
    deck: Deck | null
    activeSlide: Slide | null
  }>({
    deck: null,
    activeSlide: null,
  })

  const deckModeState = useDeckMode()

  const fetchDeck = async () => {
    try {
      const res = await getDeckById(id, {
        include: INCLUDE,
      })

      const hasV2Models = res.slides?.some(s => s.type === SlideTypes.Model)
      const hasSiteSlides = res.slides?.some(s => s.type === SlideTypes.Site)
      const hasV3Models = res.slides?.some(s => s.type === SlideTypes.Scene)
      if ((hasV2Models || hasSiteSlides) && !hasV3Models) {
        throw new LegacyDeckError()
      }

      if (res) {
        state.deck = res

        // If activeSlide is already set, update it with the fresh data
        if (state.activeSlide) {
          const updatedSlide = res.slides?.find(slide => slide.id === state.activeSlide?.id)
          if (updatedSlide) {
            state.activeSlide = updatedSlide
            onSetActiveSlide?.(updatedSlide)
          }
        }
      }
    } catch (e) {
      if (e instanceof LegacyDeckError) {
        throw e
      }

      await handleEditPermissionError()
    }
  }

  async function handleEditPermissionError() {
    state.deck = await getDeckById(id, {
      include: INCLUDE,
    })
  }

  const updateDeck = async (update: Partial<Deck>) => {
    if (!state.deck?.company_id) {
      throw new Error('Cannot update deck: company_id is required')
    }

    const { updateCompanyDeck } = useCompanyDecks(state.deck.company_id)
    await updateCompanyDeck(id, update)
    await fetchDeck()
    toast({
      title: '3D Presentation Updated',
      description: '3D Presentation was successfully updated',
      variant: 'success',
      duration: 4000,
    })
  }

  const setInitialSlide = (slideId?: number) => {
    if (slideId) {
      setActiveSlide(slideId)
    } else {
      setActiveSlide(state.deck?.slides?.[0]?.id as number)
    }
  }

  const setActiveSlide = (slideId: number) => {
    state.activeSlide = state.deck?.slides?.find(slide => slide.id === slideId) ?? null

    if (state.activeSlide) {
      onSetActiveSlide?.(state.activeSlide)
    }
  }

  const viewableSlides = computed(() => {
    const slides = state.deck?.slides?.filter(slide => slide.type !== SlideTypes.Header) ?? []

    // In meeting context, filter out video slides
    if (currentMeeting.value && !currentMeeting.value.ended_at) {
      return slides.filter(slide => slide.type !== SlideTypes.Video)
    }

    return slides
  })

  const goToNextSlide = () => {
    if (!state.activeSlide || !viewableSlides.value?.length) return

    const currentIndex = viewableSlides.value.findIndex(slide => slide.id === state.activeSlide?.id)

    if (currentIndex === -1) return

    const nextSlide = viewableSlides.value?.[currentIndex + 1]
    if (nextSlide) {
      setActiveSlide(nextSlide.id)
    }
  }

  const goToPreviousSlide = () => {
    if (!state.activeSlide || !viewableSlides.value?.length) return

    const currentIndex = viewableSlides.value.findIndex(slide => slide.id === state.activeSlide?.id)

    if (currentIndex === -1) return

    const previousSlide = viewableSlides.value?.[currentIndex - 1]
    if (previousSlide) {
      setActiveSlide(previousSlide.id)
    }
  }

  const clearActiveSlide = () => {
    state.activeSlide = null
  }

  const reorderSlides = async (payload: { id: string | number; oldIndex: number; newIndex: number }[]) => {
    if (!state.deck?.slides) return

    // Create a copy of slides to work with
    const updatedSlides = [...state.deck.slides]

    // Sort payload by oldIndex in descending order to avoid index shifting
    // issues when removing multiple items
    const sortedPayload = [...payload].sort((a, b) => b.oldIndex - a.oldIndex)

    // First, remove all slides that need to be moved (in reverse order)
    const slidesToMove: { slide: (typeof updatedSlides)[0]; newIndex: number }[] = []

    sortedPayload.forEach(({ id, newIndex }) => {
      const slideIndex = updatedSlides.findIndex(slide => slide.id === id)
      if (slideIndex !== -1) {
        const [movedSlide] = updatedSlides.splice(slideIndex, 1)
        slidesToMove.push({ slide: movedSlide, newIndex })
      }
    })

    // Then insert slides at their new positions (sorted by newIndex)
    slidesToMove
      .sort((a, b) => a.newIndex - b.newIndex)
      .forEach(({ slide, newIndex }) => {
        updatedSlides.splice(newIndex, 0, slide)
      })

    // Update all slide orders to match their new positions
    const reorderedSlides = updatedSlides.map((slide, index) => ({
      ...slide,
      order: index,
    }))

    // Update local state immediately
    state.deck = {
      ...state.deck,
      slides: reorderedSlides,
    }

    // Update active slide reference if it exists
    if (state.activeSlide) {
      const updatedActiveSlide = reorderedSlides.find(slide => slide.id === state.activeSlide?.id)
      if (updatedActiveSlide) {
        state.activeSlide = updatedActiveSlide
      }
    }

    // Make server request with all updated slide orders
    await bulkUpdateSlides(
      reorderedSlides.map(slide => ({
        id: slide.id,
        order: slide.order,
      })),
    )
  }

  return {
    ...toRefs(state),
    ...deckModeState,
    fetchDeck,
    updateDeck,
    viewableSlides,
    setActiveSlide,
    setInitialSlide,
    clearActiveSlide,
    goToNextSlide,
    goToPreviousSlide,
    reorderSlides,
  }
}
